import { useCallback, useState } from 'react';
import { useLocation, useNavigate, generatePath } from 'react-router-dom';
import { NostroTransaction } from '@ui/src';
import {
  useAddCommentToProblemTransactionMutation,
  useDeleteCommentInProblemTransactionMutation,
  useEditCommentInProblemTransactionMutation,
  useLazyGetCustomerNostroTransactionQuery,
  useLazyGetCustomersAccountBalanceQuery,
  useLazyGetCustomersTransactionLogsQuery,
  useLazyGetProblemTransactionCommentsQuery,
  useLazyGetTransactionStatementsChangeLogsQuery,
} from '../../../../customers/store/services/customers';

const useProblemNostroTransaction = ({
  transactionId,
  backPath,
  customerId,
  transaction,
}: {
  transactionId?: string;
  backPath?: string;
  customerId?: string;
  transaction?: NostroTransaction;
}) => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [fromAccountBalance, setFromAccountBalance] = useState<number>();
  const [toAccountBalance, setToAccountBalance] = useState<number>();

  const { search } = useLocation();
  const navigate = useNavigate();

  const [getNostroLogs, { isLoading: logsIsLoading }] =
    useLazyGetCustomersTransactionLogsQuery();
  const [getCustomerNostroTransaction, { isLoading, isFetching }] =
    useLazyGetCustomerNostroTransactionQuery();
  const [
    getChangeLogs,
    { isLoading: changeLogsIsLoading, isFetching: changeLogsIsFetching },
  ] = useLazyGetTransactionStatementsChangeLogsQuery();
  const [
    getFromAccountBalance,
    { isLoading: fromBalanceIsLoading, isFetching: fromBalanceIsFetching },
  ] = useLazyGetCustomersAccountBalanceQuery();
  const [
    getToAccountBalance,
    { isLoading: toBalanceIsLoading, isFetching: toBalanceIsFetching },
  ] = useLazyGetCustomersAccountBalanceQuery();
  const [
    getTransactionComments,
    { isLoading: commentsIsLoading, isFetching: commentsIsFetching },
  ] = useLazyGetProblemTransactionCommentsQuery();
  const [
    addCommentToTransaction,
    { isLoading: addCommentInProgress, isSuccess: addCommentIsSuccess },
  ] = useAddCommentToProblemTransactionMutation();
  const [
    editCommentInTransaction,
    {
      isLoading: editTransactionCommentInProgress,
      isSuccess: editTrancastionCommentIsSuccess,
    },
  ] = useEditCommentInProblemTransactionMutation();
  const [
    deleteCommentInTransaction,
    {
      isLoading: deleteCommentInTransactionInProgress,
      isSuccess: deleteCommentInTranscationIsSuccess,
    },
  ] = useDeleteCommentInProblemTransactionMutation();

  const onAddCommentToTransaction = async (text: string) => {
    await addCommentToTransaction({
      id: transactionId as string,
      url: `transactions-nostro`,
      body: {
        text,
      },
    });
  };

  const onEditCommentInTransaction = async ({
    text,
    commentId,
  }: {
    text: string;
    commentId: string;
  }) => {
    await editCommentInTransaction({
      id: transactionId as string,
      url: `transactions-nostro`,
      commentId,
      body: {
        text,
      },
    });
  };

  const onDeleteCommentInTransaction = async (commentId: string) => {
    await deleteCommentInTransaction({
      id: transactionId as string,
      url: `transactions-nostro`,
      commentId,
    });
  };

  const getTransactionCommentsAction = async (cursor?: string) => {
    await getTransactionComments({
      id: transactionId as string,
      url: `transactions-nostro`,
      cursor: cursor || '',
    });
  };

  const getNostroLogsAction = useCallback(() => {
    getNostroLogs(transactionId as string);
  }, [transactionId]);

  const getChangeLogsAction = useCallback(
    (cursor?: string) => {
      getChangeLogs(
        `?take=20&entityType=TRANSACTION&entityId=${transactionId}${
          cursor ? `&cursor=${cursor}` : ''
        }`,
      );
    },
    [transactionId],
  );

  const getFromAccountBalanceAction = useCallback(async () => {
    const response = (await getFromAccountBalance({
      id: transaction?.senderAccount.id,
      currencyId: transaction?.currencyId as string,
    })) as { data: { data: { edges: { node: { value: number } }[] } } };

    if (response.data) {
      setFromAccountBalance(response.data.data.edges[0].node.value);
    }
  }, [transaction]);

  const getToAccountBalanceAction = useCallback(async () => {
    const response = (await getToAccountBalance({
      id: transaction?.recipientAccount.id,
      currencyId: transaction?.currencyId as string,
    })) as { data: { data: { edges: { node: { value: number } }[] } } };

    if (response.data) {
      setToAccountBalance(response.data.data.edges[0].node.value);
    }
  }, [transaction]);

  const getNostroTransactionById = useCallback(() => {
    getCustomerNostroTransaction(transactionId as string);
    getNostroLogs(transactionId as string);
  }, [transactionId]);

  const handleClose = () => {
    if (backPath) {
      setPanelIsOpen(false);

      setTimeout(() => {
        navigate(
          generatePath(`${backPath}${search || ''}`, {
            customerId,
          }),
        );
      }, 100);
    }
  };

  return {
    setPanelIsOpen,
    search,
    panelIsOpen,
    handleClose,
    logsIsLoading,
    isLoading,
    isFetching,
    changeLogsIsLoading,
    changeLogsIsFetching,
    getChangeLogsAction,
    getFromAccountBalanceAction,
    getToAccountBalanceAction,
    fromBalanceIsLoading,
    fromBalanceIsFetching,
    toBalanceIsLoading,
    toBalanceIsFetching,
    fromAccountBalance,
    toAccountBalance,
    getNostroLogsAction,
    getTransactionCommentsAction,
    commentsIsLoading,
    commentsIsFetching,
    onAddCommentToTransaction,
    addCommentInProgress,
    addCommentIsSuccess,
    onEditCommentInTransaction,
    editTransactionCommentInProgress,
    editTrancastionCommentIsSuccess,
    onDeleteCommentInTransaction,
    deleteCommentInTransactionInProgress,
    getNostroTransactionById,
    deleteCommentInTranscationIsSuccess,
  };
};

export default useProblemNostroTransaction;
